import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Dialog, DialogContent } from '@material-ui/core'
import { Spinner } from 'ui'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import useStore from 'store'
import { cleanImportedText } from './textUtils'
import { APPROVMENT_STATUS_MAP } from './approvmentStatusMap'
import { CurrentStatusBadge, StatusBadgeIcon } from './ProtocolViewModal'

import {
  DialogHeaderBlue,
  Header,
  StyledClose,
  StyledIconButtonWhite,
  CategoryListTitleContainer,
  CategoryListTitleSubContainer1,
  HeaderTitle1,
  NewCategoryBody,
  NewCategoryContainerColumn,
  NewCategoryContainer,
} from './CategoryModal.styles'
// Shared with the full edit screen so both screens look the same.
import {
  ProtocolContainerActions,
  CategoryListTitleSubContainer2,
  HeaderTitle2,
  HeaderDialogButton,
  MetaItem,
  ImageHeaderBlue,
  ImageHeader,
  ProtocolEditContainer,
  CloseButton,
  SpinnerWrap,
  FieldLabel,
  FieldRequired,
  FieldWrapper,
  StyledTextarea,
  StatusTitle,
  StatusHeaderRow,
  NotPendingApprovalNotice,
  StatusContent,
  StatusCard,
} from './ProtocolEditModal'

import ApprovedStatusIcon from 'assets/icons/protocols/icon10.svg'
import DocIcon from 'assets/icons/protocols/icon12.svg'
import SaveIcon from 'assets/icons/protocols/icon26.svg'
import RejectedStatusIcon from 'assets/icons/protocols/icon27.svg'

// Protocol status ids sent to PUT /protocols/{id}/status (distinct from approvment status ids).
const APPROVED_PROTOCOL_STATUS = 3
const REJECTED_PROTOCOL_STATUS = 4

const STATUS_CARDS = [
  { titleKey: 'status_approved', value: APPROVED_PROTOCOL_STATUS, icon: ApprovedStatusIcon, selectedBg: '#dcfce7', selectedColor: '#0d542b', selectedBorder: '#00c951' },
  { titleKey: 'status_rejected', value: REJECTED_PROTOCOL_STATUS, icon: RejectedStatusIcon, selectedBg: '#ffe2e2', selectedColor: '#82181a', selectedBorder: '#fb2c36' },
]

// Status previously selected by the current approver, shown above the blue status panel.
const CurrentApproverStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 8px;
  color: #000;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: right;
`

// Approve/Reject screen (green icon) - loads only the data it needs and saves via the status endpoint.
const ProtocolEditStatusModal = ({ open, protocolId, onClose, onSaved }: { open: boolean; protocolId: number | null; onClose: () => void; onSaved?: () => void }) => {
  const { t } = useTranslation('protocols')
  const { protocolsStore } = useStore()
  const data = protocolsStore.protocolStatusEdit
  const loading = protocolsStore.loadingProtocolStatusEdit

  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setSelectedStatusId(null)
    setRejectReason('')
  }, [data])

  const handleSave = async () => {
    if (!protocolId || selectedStatusId === null) return
    setSaving(true)
    try {
      const res = await protocolsStore.updateProtocolStatus(protocolId, {
        protocolStatusId: selectedStatusId,
        rejectReason: selectedStatusId === REJECTED_PROTOCOL_STATUS ? rejectReason : '',
      })
      if (res) {
        onClose()
        onSaved?.()
      }
    } catch (err) {
      console.error('ProtocolEditStatusModal.handleSave failed', err)
    } finally {
      setSaving(false)
    }
  }

  const modifiedStr = data?.modifiedDate ? new Date(data.modifiedDate).toLocaleDateString('he-IL') : ''
  const protocolName = cleanImportedText(data?.title)
  const previousStatus = data?.myApprovmentStatusId ? APPROVMENT_STATUS_MAP[data.myApprovmentStatusId] : undefined
  const isSaveDisabled = saving || !data?.canApprove || selectedStatusId === null
    || (selectedStatusId === REJECTED_PROTOCOL_STATUS && !rejectReason.trim())

  return (
    <Dialog style={{ zIndex: 1200 }} open={open} fullWidth maxWidth="md" scroll="body">
      <DialogHeaderBlue>
        <Header style={{ background: 'linear-gradient(90deg, #9810FA 0%, #8200DB 100%)' }}>
          <CategoryListTitleContainer>
            <CategoryListTitleSubContainer2>
              <ImageHeader src={DocIcon} />
            </CategoryListTitleSubContainer2>
            <CategoryListTitleSubContainer1>
              <HeaderTitle1 style={{ color: 'white' }}>
                {`${t`edit_status_title`}${protocolName ? `: ${protocolName}` : ''}`}
              </HeaderTitle1>
              <HeaderTitle2>
                {data?.version && <MetaItem>{t`version`} {data.version} • </MetaItem>}
                {modifiedStr && <MetaItem>{t`last_updated`} {modifiedStr}</MetaItem>}
              </HeaderTitle2>
            </CategoryListTitleSubContainer1>
          </CategoryListTitleContainer>
          <StyledIconButtonWhite onClick={onClose}><StyledClose /></StyledIconButtonWhite>
        </Header>
      </DialogHeaderBlue>

      <DialogContent>
        <ProtocolEditContainer>
          {loading && <SpinnerWrap><Spinner /></SpinnerWrap>}

          {!loading && data && (
            <NewCategoryBody>
              {/* Wrapper keeps the label and the panel together - the body's flex gap would split them. */}
              <div style={{ width: '100%' }}>
              {previousStatus && (
                <CurrentApproverStatus>
                  {t`approver_current_status`}
                  <CurrentStatusBadge $color={previousStatus.color} $bg={previousStatus.bg}>
                    <StatusBadgeIcon src={previousStatus.icon} /> {t(previousStatus.titleKey)}
                  </CurrentStatusBadge>
                </CurrentApproverStatus>
              )}

              <NewCategoryContainer style={{ marginTop: previousStatus ? 0 : '16px', borderRadius: '8px', border: '2px solid #BEDBFF', background: '#EFF6FF', flexDirection: 'column', gap: '8px', padding: '12px' }}>
                <StatusHeaderRow>
                  <StatusTitle>{t`protocol_status`}</StatusTitle>
                </StatusHeaderRow>
                <StatusContent>
                  {STATUS_CARDS.map(item => (
                    <StatusCard
                      key={item.value}
                      selected={selectedStatusId === item.value}
                      selectedBg={item.selectedBg}
                      selectedColor={item.selectedColor}
                      selectedBorder={item.selectedBorder}
                      disabled={!data.canApprove}
                      onClick={() => data.canApprove && setSelectedStatusId(item.value)}
                    >
                      <ImageHeader src={item.icon} style={{ width: 18, height: 18 }} />
                      {t(item.titleKey)}
                    </StatusCard>
                  ))}
                </StatusContent>
              </NewCategoryContainer>
              </div>

              {data.protocolStatusId === 1 && (
                <NotPendingApprovalNotice>{t`notice_draft_cannot_approve`}</NotPendingApprovalNotice>
              )}
              {data.protocolStatusId === 3 && (
                <NotPendingApprovalNotice>{t`notice_published_cannot_approve`}</NotPendingApprovalNotice>
              )}

              {selectedStatusId === REJECTED_PROTOCOL_STATUS && (
                <NewCategoryContainerColumn style={{ marginTop: '16px' }}>
                  <FieldWrapper>
                    <FieldLabel>{t`reject_reason`}<FieldRequired>*</FieldRequired></FieldLabel>
                    <StyledTextarea
                      value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      placeholder={t`reject_reason_placeholder`}
                      dir="rtl"
                    />
                  </FieldWrapper>
                </NewCategoryContainerColumn>
              )}
            </NewCategoryBody>
          )}
        </ProtocolEditContainer>

        <ProtocolContainerActions style={{ paddingRight: 0 }}>
          <CloseButton onClick={onClose}>{t`close`}</CloseButton>
          <HeaderDialogButton onClick={handleSave} disabled={isSaveDisabled}>
            {saving ? <Spinner size={16} /> : <><ImageHeaderBlue src={SaveIcon} /> {t`save_changes`}</>}
          </HeaderDialogButton>
        </ProtocolContainerActions>
      </DialogContent>
    </Dialog>
  )
}

export default observer(ProtocolEditStatusModal)
